import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Copy,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Textarea } from '@/app/components/ui/textarea';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useGearRequestTemplates,
  useCreateGearRequestTemplate,
  useUpdateGearRequestTemplate,
  useDeleteGearRequestTemplate,
} from '@/hooks/use-gear-request-templates';
import type { GearRequestTemplate } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

interface TemplateLine {
  description: string;
  quantity: number;
  notes: string;
}

const EMPTY_LINE: TemplateLine = { description: '', quantity: 1, notes: '' };

export function GearRequestTemplatesPage() {
  const { canManageGear } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectType = searchParams.get('project_type') || '';
  const category = searchParams.get('category') || '';

  const { data, isLoading, isError, refetch } = useGearRequestTemplates({
    project_type: projectType,
    category,
  });
  const createMutation = useCreateGearRequestTemplate();
  const updateMutation = useUpdateGearRequestTemplate();
  const deleteMutation = useDeleteGearRequestTemplate();

  const templates = data?.results || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<GearRequestTemplate | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formProjectType, setFormProjectType] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formLines, setFormLines] = useState<TemplateLine[]>([{ ...EMPTY_LINE }]);

  const resetForm = useCallback(() => {
    setEditingTemplate(null);
    setFormName('');
    setFormDescription('');
    setFormProjectType('');
    setFormCategory('');
    setFormLines([{ ...EMPTY_LINE }]);
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setDialogOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((tpl: GearRequestTemplate) => {
    setEditingTemplate(tpl);
    setFormName(tpl.name);
    setFormDescription(tpl.description);
    setFormProjectType(tpl.project_type);
    setFormCategory(tpl.category);
    setFormLines(
      tpl.lines.length > 0
        ? tpl.lines.map((l) => ({
            description: l.description,
            quantity: l.quantity,
            notes: l.notes,
          }))
        : [{ ...EMPTY_LINE }]
    );
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    const lines = formLines.filter((l) => l.description.trim());
    const payload = {
      name: formName,
      description: formDescription,
      project_type: formProjectType,
      category: formCategory,
      lines: lines.map((l) => ({
        description: l.description,
        quantity: l.quantity,
        notes: l.notes,
      })),
    };

    if (editingTemplate) {
      await updateMutation.mutateAsync({ id: editingTemplate.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    setDialogOpen(false);
    resetForm();
  }, [
    formName,
    formDescription,
    formProjectType,
    formCategory,
    formLines,
    editingTemplate,
    createMutation,
    updateMutation,
    resetForm,
  ]);

  const handleDelete = useCallback(
    async (id: number) => {
      if (!window.confirm('Delete this template?')) return;
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );

  const addLine = useCallback(() => {
    setFormLines((prev) => [...prev, { ...EMPTY_LINE }]);
  }, []);

  const removeLine = useCallback((index: number) => {
    setFormLines((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateLine = useCallback(
    (index: number, field: keyof TemplateLine, value: string | number) => {
      setFormLines((prev) =>
        prev.map((l, i) => (i === index ? { ...l, [field]: value } : l))
      );
    },
    []
  );

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Request Templates
          </h1>
          <p className="text-gray-600 mt-1">
            Reusable gear request packages for common needs
          </p>
        </div>
        <div className="flex gap-2">
          {canManageGear && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          )}
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              placeholder="Filter by project type..."
              value={projectType}
              onChange={(e) => updateFilter('project_type', e.target.value)}
              className="w-full sm:w-[200px]"
            />
            <Input
              placeholder="Filter by category..."
              value={category}
              onChange={(e) => updateFilter('category', e.target.value)}
              className="w-full sm:w-[200px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Templates table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load templates. Please try again.
            </div>
          ) : templates.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No templates found.</p>
              {canManageGear && (
                <Button onClick={openCreate} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Template
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Project Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Lines</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell className="font-medium">{tpl.name}</TableCell>
                    <TableCell>
                      {tpl.project_type ? (
                        <Badge variant="secondary">{tpl.project_type}</Badge>
                      ) : (
                        <span className="text-gray-400">{'\u2014'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {tpl.category ? (
                        <Badge variant="outline">{tpl.category}</Badge>
                      ) : (
                        <span className="text-gray-400">{'\u2014'}</span>
                      )}
                    </TableCell>
                    <TableCell>{tpl.lines.length}</TableCell>
                    <TableCell>{tpl.created_by_username || '\u2014'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Link to={`/gear-requests/templates/${tpl.id}/use`}>
                          <Button variant="ghost" size="sm" title="Create request from template">
                            <Copy className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(tpl)}
                          title="Edit template"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(tpl.id)}
                          title="Delete template"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'New Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? 'Update this gear request template.'
                : 'Create a reusable gear request template with pre-defined line items.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tpl-name">Name *</Label>
                <Input
                  id="tpl-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g., Standard PPE Kit"
                  required
                />
              </div>
              <div>
                <Label htmlFor="tpl-project-type">Project Type</Label>
                <Input
                  id="tpl-project-type"
                  value={formProjectType}
                  onChange={(e) => setFormProjectType(e.target.value)}
                  placeholder="e.g., construction"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tpl-category">Category</Label>
                <Input
                  id="tpl-category"
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  placeholder="e.g., safety"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="tpl-description">Description</Label>
              <Textarea
                id="tpl-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Describe this template..."
                rows={2}
              />
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items</Label>
                <Button variant="outline" size="sm" onClick={addLine}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Line
                </Button>
              </div>
              <div className="space-y-2">
                {formLines.map((line, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      value={line.description}
                      onChange={(e) => updateLine(index, 'description', e.target.value)}
                      placeholder="Item description"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={line.quantity}
                      onChange={(e) =>
                        updateLine(index, 'quantity', parseInt(e.target.value, 10) || 1)
                      }
                      className="w-20"
                      min={1}
                    />
                    <Input
                      value={line.notes}
                      onChange={(e) => updateLine(index, 'notes', e.target.value)}
                      placeholder="Notes"
                      className="w-32"
                    />
                    {formLines.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingTemplate ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
